import React, { CSSProperties, InputHTMLAttributes, ReactElement, ReactNode, Ref, useEffect, useState } from 'react';
import { css, cx } from '@emotion/css';
import { useMemoizedId } from './utils';
import defaultTheme from './theme/theme';
import cvar from './theme/cvar';
import SuccessSvg from './icons/SuccessSvg';
import WarningSvg from './icons/WarningSvg';
import ErrorSvg from './icons/ErrorSvg';
import { PublicComponentProps } from './types';

const isTextTruthy = (val: any) => val !== undefined && val !== null && val.toString() !== '';

type Status = 'success' | 'warning' | 'error';

type BaseChangeHandlerSignature = (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> & { isValid: boolean },
) => void;

interface TextInputBaseProps<T> extends InputHTMLAttributes<T>, PublicComponentProps {
  status?: Status;
  helpText?: ReactNode;
  id?: string;
  inputStyle?: CSSProperties;
  label?: string;
  onChange: (event: React.ChangeEvent<T> & { isValid: boolean }) => void;
  rightAddon?: ReactElement;
  type?: 'text' | 'number' | 'password' | 'url' | 'search' | 'email' | 'tel' | 'textarea';
}

export const formGroupCss = css`
  position: relative;
  margin-bottom: ${cvar('spacing-16')};

  input {
    box-shadow: none;
    padding: ${cvar('spacing-16')};
    transition: all 0.2s ease-out;

    &:focus {
      outline: none;
      box-shadow: none;
      border-color: ${cvar('color-border-interactive')};
    }
  }
  textarea {
    box-shadow: none;
    padding: ${cvar('spacing-24')} ${cvar('spacing-16')} ${cvar('spacing-12')};
    height: auto;

    &:focus {
      outline: none;
      box-shadow: none;
      border-color: ${cvar('color-border-interactive')};
    }
  }
  label {
    color: ${cvar('color-text-label')};
    font-weight: normal;
    position: absolute;
    left: ${cvar('spacing-16')};
    top: ${cvar('spacing-16')};
    line-height: 16px;
    margin-bottom: 0;
    transition: all 0.2s ease-out;
    z-index: 5;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 90%;
  }
`;

export const formGroupActiveCss = css`
  input {
    padding: ${cvar('spacing-24')} ${cvar('spacing-16')} ${cvar('spacing-8')};
  }
  textarea {
    padding-top: ${cvar('spacing-24')};
  }
  label {
    top: ${cvar('spacing-4')};
    font-size: 12px;
  }
`;

export const controlLabelCss = css`
  margin-bottom: 0;
  font-size: inherit;
  line-height: 1.5;
`;

const textAreaLabel = css`
  top: 1px;
  z-index: 5;
  background-color: ${cvar('color-background')};
  padding: 4px;
`;

const isRequired = css`
  label:after {
    content: ' *';
    color: ${cvar('color-required-asterisk')};
  }
`;

const isDisabled = css`
  input {
    color: ${cvar('color-text-label')};
    background: ${cvar('color-textfield-disabled')};
  }
`;

const formControl = css`
  height: ${cvar('spacing-48')};
  border: 1px solid ${cvar('color-border-default')};
`;

const inputGroup = css`
  zindex: 0;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;

  > .crc-text-field {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    margin-bottom: 0;

    + .crc-text-field {
      margin-left: 1px;
    }
  }
`;
const inputGroupButton = css`
  > button {
    padding: ${cvar('spacing-12')} ${cvar('spacing-24')};
    border-bottom-right-radius: ${cvar('spacing-2')};
    border-top-right-radius: ${cvar('spacing-2')};
    line-height: 18px;
    height: ${cvar('spacing-48')};
    border-bottom-left-radius: 0px;
    border-top-left-radius: 0px;
  }
`;

const helpBlock = css`
  display: block;
  margin-top: ${cvar('spacing-4')};
`;

const textfieldColorMap: Record<Status, keyof typeof defaultTheme> = {
  success: 'color-border-success',
  warning: 'color-border-warning',
  error: 'color-border-error',
};

const statusIconSvg: Record<Status, ReactNode> = {
  success: <SuccessSvg />,
  warning: <WarningSvg />,
  error: <ErrorSvg />,
};

const statusCss = (status: Status) => css`
  input {
    box-shadow: none;
    padding-right: ${cvar('spacing-32')};
    &:focus {
      border-color: ${cvar(textfieldColorMap[status])};
    }
  }

  .crc-text-field {
    border-color: ${cvar(textfieldColorMap[status])};
  }

  svg {
    position: absolute;
    right: ${cvar('spacing-12')};
    top: ${cvar('spacing-16')};
    color: ${cvar(textfieldColorMap[status])};
    transition: all 0.2s ease-out;
  }
`;

const TextInputBase = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextInputBaseProps<HTMLInputElement | HTMLTextAreaElement>
>(
  (
    {
      autoFocus,
      className,
      disabled,
      helpText,
      id,
      inputStyle,
      label,
      name,
      onBlur,
      onChange,
      onClick,
      onFocus,
      onKeyDown,
      placeholder,
      required,
      rightAddon,
      status,
      style,
      type,
      value,
      pattern,
      ...rest
    },
    ref,
  ) => {
    const [float, setFloat] = useState(isTextTruthy(value));

    const uniqueId = useMemoizedId({ label: `${name}-${placeholder}-${label}`, id });

    useEffect(() => {
      setFloat(isTextTruthy(value));
    }, [setFloat, value]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFloat(isTextTruthy(value));
      onBlur && onBlur(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFloat(true);
      onFocus && onFocus(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const isValid = !pattern || !!e.target.value.match(new RegExp(`^${pattern}$`));
      onChange && onChange({ ...e, isValid });
    };

    const inputId = id || uniqueId;
    const isTextArea = type === 'textarea';

    const wrapperClassNames = cx('crc-text-input', formGroupCss, className, {
      [formGroupActiveCss]: !!label && float,
      [statusCss(status as Status)]: !!status,
      [isRequired]: !!required,
      [isDisabled]: !!disabled,
    });

    const inputProps = {
      ...rest,
      autoFocus,
      disabled,
      id: inputId,
      name,
      onBlur: handleBlur,
      onChange: handleChange,
      onClick,
      onFocus: handleFocus,
      onKeyDown,
      placeholder,
      required,
      style: inputStyle,
      value,
      pattern,
      type: !isTextArea ? type : undefined,
    };

    const inputElement = isTextArea ? (
      <textarea {...inputProps} className={cx(formControl, 'crc-text-field')} ref={ref as Ref<HTMLTextAreaElement>} />
    ) : (
      <input {...inputProps} className={cx(formControl, 'crc-text-field')} ref={ref as Ref<HTMLInputElement>} />
    );

    const labelElement = label ? (
      <label className={cx(controlLabelCss, { [textAreaLabel]: isTextArea })} htmlFor={inputId} title={label}>
        {label}
      </label>
    ) : null;

    return (
      <div className={wrapperClassNames} style={style}>
        <div className={inputGroup}>
          {inputElement}
          {labelElement}
          {rightAddon && <span className={inputGroupButton}>{rightAddon}</span>}
        </div>
        {!!status && statusIconSvg[status]}
        {helpText ? <small className={helpBlock}>{helpText}</small> : null}
      </div>
    );
  },
);

TextInputBase.displayName = 'TextField';

TextInputBase.defaultProps = {
  autoFocus: false,
  className: '',
  disabled: false,
  required: false,
  type: 'text',
  value: '',
};

export interface TextFieldProps extends TextInputBaseProps<HTMLInputElement> {
  /** Determines the HTML5 input type of the input. One of text, number, password, url, search, email, or tel. */
  type?: 'text' | 'number' | 'password' | 'url' | 'search' | 'email' | 'tel';
}

export interface TextAreaProps extends TextInputBaseProps<HTMLTextAreaElement> {}

type BaseRefType = Ref<HTMLInputElement | HTMLTextAreaElement>;

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => (
  <TextInputBase {...props} onChange={props.onChange as BaseChangeHandlerSignature} ref={ref as BaseRefType} />
));

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>((props, ref) => (
  <TextInputBase
    {...props}
    type="textarea"
    onChange={props.onChange as BaseChangeHandlerSignature}
    ref={ref as BaseRefType}
  />
));
